import { observer } from 'mobx-react-lite';
import { createContext, type ReactNode, useEffect, useRef } from 'react';
import { useSelectedSchoolYear } from '../hooks/useSelectedSchoolYear';
import { eventJobRunningStore } from '../stores/eventJobRunningStore';
import { EventJobStatus, useEventJobsQuery } from '../types/planung-graphql-client-defs';

export const EventJobRunningContext = createContext<boolean>(false);

/**
 * Führt die EventJobs-Query zentral aus (mit `network-only`) und stellt das
 * `isRunning`-Flag über Context bereit. Dadurch entsteht nur eine einzige
 * Netzwerk-Abfrage statt einer pro Consumer.
 *
 * Es wird NICHT dauerhaft gepollt (das wäre alle 5s eine Anfrage, auch wenn nie
 * ein Job läuft). Stattdessen:
 *  - einmalig beim Mount geprüft, ob bereits ein Job läuft (z.B. aus einer
 *    anderen Session), und
 *  - sobald eine Aktion einen Job auslöst (`eventsApi` → `markStarted()` setzt das
 *    optimistische Flag), wird sofort live nachgeladen.
 * Erst ab `isRunning === true` läuft der 5s-Tick – und nur so lange, bis der echte
 * Status wieder `false` meldet. Im Ruhezustand gibt es also keinerlei Polling.
 * (Sobald Subscriptions verfügbar sind, kann der Tick ganz entfallen.)
 */
export const EventJobRunningProvider = observer(({ children }: { children: ReactNode }) => {
  const selectedSchoolYear = useSelectedSchoolYear();

  const [{ data }, refetch] = useEventJobsQuery({
    variables: { runningOnly: true },
    pause: !selectedSchoolYear,
    requestPolicy: 'network-only',
  });

  const realRunning = data?.eventJobs.some((job) => job.status === EventJobStatus.Running) ?? false;

  // Optimistisches Flag: wird von `eventsApi` beim Auslösen eines Jobs sofort gesetzt,
  // damit `isEventJobRunning` ohne Poll-Latenz sofort `true` ist.
  const optimisticRunning = eventJobRunningStore.optimisticRunning;
  const isRunning = optimisticRunning || realRunning;

  // refetch in einem Ref halten, damit das Polling-Intervall nicht bei jedem
  // Re-Render zurückgesetzt wird (sonst feuert es u. U. nie).
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // Der 5s-Tick läuft NUR, solange ein Job läuft. Kickoff erfolgt entweder durch das
  // optimistische Flag (Aktions-Callback) oder durch einen beim Mount erkannten Job.
  // Sobald `isRunning` wieder `false` ist, wird das Intervall abgeräumt → kein Polling
  // im Ruhezustand.
  useEffect(() => {
    if (!selectedSchoolYear || !isRunning) return;
    const id = setInterval(() => refetchRef.current({ requestPolicy: 'network-only' }), 5000);
    return () => clearInterval(id);
  }, [selectedSchoolYear, isRunning]);

  // Sobald ein Job optimistisch ausgelöst wurde, sofort den echten Status nachladen
  // (statt bis zum nächsten 5s-Tick zu warten).
  useEffect(() => {
    if (optimisticRunning) refetchRef.current({ requestPolicy: 'network-only' });
  }, [optimisticRunning]);

  // Sobald der echte Status einen laufenden Job bestätigt, das optimistische Flag
  // abräumen – ab dann steuert die echte Datenlage (inkl. Beenden des Jobs).
  useEffect(() => {
    if (realRunning) eventJobRunningStore.clear();
  }, [realRunning]);

  return <EventJobRunningContext.Provider value={isRunning}>{children}</EventJobRunningContext.Provider>;
});

EventJobRunningProvider.displayName = 'EventJobRunningProvider';
